import { MessageFilled } from "@ant-design/icons";
import { Flex } from "antd/lib";
import { FC, useMemo } from "react";
import { Point, TaskStatus } from "tm-shared/types";

import { GalleryUploader } from "@/domains/media";
import { usePointDistance } from "@/domains/point";
import { StatusCycleButton } from "@/domains/tasks";
import { useAppSelector } from "@/hooks/redux";
import { OutlineIconButton, PrimaryText } from "@/shared/ui";
import { buildPhotoCaptions } from "@/utils/photo/captions";

import { StyledBottomSheetTop } from "./Styled";

interface BottomSheetContentProps {
    title: string;
    activeStatus: TaskStatus;
    savedPhotos: string[];
    activeItemId: string;
    pointId: string;
    captionInfo: {
        point: Point;
        taskName: string;
    };
    onChangeTaskStatus: (value: TaskStatus) => void;
    onUploadPhoto: (photo: string) => void;
    onDeletePhoto: (photoId: string) => void;
    onOpenCommentModal: () => void;
}

export const BottomSheetContent: FC<BottomSheetContentProps> = ({
    title,
    activeStatus,
    savedPhotos,
    activeItemId,
    pointId,
    captionInfo,
    onChangeTaskStatus,
    onOpenCommentModal,
    onUploadPhoto,
    onDeletePhoto,
}) => {
    const { firstName, lastName } = useAppSelector((state) => state.user);

    const { point, taskName } = captionInfo;

    const { distance } = usePointDistance({
        pointGeo: point.market.building.geo,
    });

    const captions = useMemo(
        () =>
            buildPhotoCaptions({
                pointName: point.name,
                taskName,
                userFullName: `${firstName} ${lastName}`,
                distance,
            }),
        [distance, firstName, lastName, point.name, taskName],
    );

    return (
        <>
            <StyledBottomSheetTop>
                <PrimaryText>{title}</PrimaryText>
                <Flex gap="20px">
                    <OutlineIconButton
                        icon={<MessageFilled />}
                        onClick={() => onOpenCommentModal()}
                        disabled={activeStatus === TaskStatus.Incomplete}
                    />

                    <StatusCycleButton
                        value={activeStatus}
                        onChange={onChangeTaskStatus}
                        states={[TaskStatus.Incomplete, TaskStatus.Rejected]}
                        disabled={activeStatus === TaskStatus.Completed}
                    />
                </Flex>
            </StyledBottomSheetTop>

            <GalleryUploader
                previewTitle={title}
                onDelete={onDeletePhoto}
                onUpload={onUploadPhoto}
                activeItemId={activeItemId}
                pointId={pointId}
                savedPhotos={savedPhotos}
                disabled={activeStatus === TaskStatus.Rejected}
                captionLines={captions}
            />
        </>
    );
};
